import { BAGGAGE, TRACEPARENT } from '@onyx/constants';
import { sendEvent } from '@onyx/observability-v2/client';
import {
  generateTraceParent,
  getTelemetryBaggage,
} from '@onyx/observability-v2/utils';
import { isClient } from '@onyx/utils/browser';
import { handlePromise } from '@onyx/utils/promises';

import { appendHeaders } from './append-header';
import { isSameOrigin } from './is-same-origin';

const getStatusType = (status: Response['status']) => {
  if (status >= 300 && status < 400) {
    return 'redirect';
  }
  if (status >= 400 && status < 500) {
    return 'client';
  }
  return 'server';
};

const appendTracingHeaders = (
  input: RequestInfo | URL,
  init: RequestInit | undefined
): { requestInit: RequestInit | undefined; traceId?: string } => {
  const telemetryBaggage = getTelemetryBaggage();

  if (!isClient()) {
    return {
      requestInit: {
        ...init,
        headers: appendHeaders(init?.headers, {
          [BAGGAGE]: telemetryBaggage,
        }),
      },
    };
  }
  if (!isSameOrigin(input)) {
    return { requestInit: init };
  }
  const { traceParent, traceId } = generateTraceParent();
  return {
    requestInit: {
      ...init,
      headers: appendHeaders(init?.headers, {
        [TRACEPARENT]: traceParent,
        [BAGGAGE]: telemetryBaggage,
      }),
    },
    traceId,
  };
};

export type FetchResponseError = {
  kind: 'response-error';
  type: ReturnType<typeof getStatusType>;
  text: Awaited<ReturnType<Response['text']>>;
} & Pick<Response, 'status' | 'statusText' | 'headers'>;
export type FetchRequestError = { kind: 'request-error'; error: Error };

export type FetchError = FetchRequestError | FetchResponseError;
export type OkayedResponse = Omit<Response, 'ok'> & { ok: true };

export type HandleFetchReturn = Promise<
  [null, OkayedResponse] | [FetchError, null]
>;

export type HandleFetch = (
  ...parameters: Parameters<typeof fetch>
) => HandleFetchReturn;

const isResponseOk = (response: Response): response is OkayedResponse =>
  response.ok;

export const handleFetch: HandleFetch = async (input, init) => {
  const { requestInit, traceId } = appendTracingHeaders(input, init);

  const startTime = performance.now();
  const [error, response] = await handlePromise(fetch(input, requestInit));
  const endTime = performance.now();

  if (isClient()) {
    const urlString = input instanceof Request ? input.url : String(input);
    sendEvent('onyx.client.fetch', {
      traceId,
      url: urlString,
      durationMs: endTime - startTime,
      responseStatus: response?.status ?? -1,
    });
  }

  if (error) {
    return [{ kind: 'request-error', error }, null];
  }

  if (isResponseOk(response)) {
    return [null, response];
  }
  const { status, statusText, headers } = response;
  return [
    {
      kind: 'response-error',
      type: getStatusType(status),
      text: await response.text(),
      status,
      statusText,
      headers,
    },
    null,
  ];
};
